import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { PermissionEnum } from '@thro-types/access-handler';
import { User } from '@thro-types/users';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Navigate, RouteProps, useLocation, useParams } from 'react-router-dom';

import USER_QUERY from 'gql/queries/currentUser.gql';

import { SubjectType } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { ACLHelper, UserType } from 'utils/ACLHelper';
import { useLayout } from 'utils/hooks/useLayout';

interface Props extends Omit<RouteProps, 'component'> {
  requiredPermission?: PermissionEnum;
  requires?: {
    roles?: string[];
    entitlements?: string[];
    fpwm_admin?: boolean;
    userType?: UserType;
  };
  user: User;
  userPermissions?: string[];
  onlyAnonymous?: boolean;
  notAnonymous?: boolean;
  companyUser?: boolean;
  children: JSX.Element;
  canAccess?: boolean;
}
export default function PrivateRoute({
  children,
  requiredPermission,
  requires,
  user,
  userPermissions,
  onlyAnonymous,
  notAnonymous,
  companyUser,
  canAccess
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const client = useApolloClient();
  const userQuery = client.readQuery({ query: USER_QUERY });
  const { id: companyId } = useParams();
  if (!user && userQuery && userQuery.currentUser) {
    user = userQuery.currentUser;
  }

  // ! We wont render the Layout if we have no user
  const { setWithoutLayout } = useLayout();

  const aclHelper = new ACLHelper(user);

  const hasRequiredPermission = requiredPermission
    ? userPermissions?.some(userPermission => {
        return userPermission === requiredPermission;
      })
    : false;
  const isAuthorized = user
    ? aclHelper.hasOne('entitlement', requires?.entitlements) ||
      aclHelper.hasOne('role', requires?.roles) ||
      (requires?.userType === UserType.INCOMING && aclHelper.isInternationalOfficeUser()) ||
      (requires?.userType === UserType.COMPANY && aclHelper.isCompanyUser())
    : false;

  const isFPWMAdmin = () => {
    if (requires?.fpwm_admin) return aclHelper.isSubjectSelectionAdmin(false, SubjectType.Fwpm);
  };

  if (aclHelper.isSuperUser()) return children;
  if (isAuthorized || isFPWMAdmin() || hasRequiredPermission || canAccess) return children;
  if (!user && onlyAnonymous) return children;
  if (user && notAnonymous) return children;
  if (companyUser && aclHelper.isCompanyUser(companyId)) return children;

  if (!user) {
    setWithoutLayout(true);
    localStorage.setItem('redirect-after-login', location?.pathname || '/');
    return <Navigate to={{ pathname: '/login' }} replace state={{ from: location }} />;
  }

  if (!onlyAnonymous) {
    dispatch(AlertActions.error(t(translations.authorization.notAuthorizedRoute())));
    Sentry.captureEvent({
      level: 'debug',
      message: 'User tried to access a restricted Route',
      extra: {
        user,
        isAuthorized,
        onlyAnonymous,
        notAnonymous
      }
    });
  }

  return <Navigate to={{ pathname: '/' }} state={{ from: location }} />;
}
